import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SystemUpdateAlt as UpdateIcon
} from '@mui/icons-material';
import api from '../services/api';

const AppUpdatesPage = () => {
  const [updates, setUpdates] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [formData, setFormData] = useState({
    application_id: '',
    enabled: true,
    current_version: '',
    skip: true,
    duration: 120,
    timeout: 0,
    image: '',
    title: 'Внимание!',
    description: '',
    text_button: 'СКАЧАТЬ',
    url: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadUpdates();
    loadApplications();
  }, []);

  const loadUpdates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/app-updates');
      setUpdates(response.data);
    } catch (err) {
      setError('Ошибка загрузки настроек обновлений');
    } finally {
      setLoading(false);
    }
  };

  const loadApplications = async () => {
    try {
      const response = await api.get('/admin/applications');
      setApplications(response.data);
    } catch (err) {
      setError('Ошибка загрузки приложений');
    }
  };

  const handleOpenDialog = (update = null) => {
    if (update) {
      setEditingUpdate(update);
      setFormData({
        application_id: update.application_id,
        enabled: update.enabled,
        current_version: update.current_version,
        skip: update.skip,
        duration: update.duration,
        timeout: update.timeout,
        image: update.image || '',
        title: update.title,
        description: update.description,
        text_button: update.text_button,
        url: update.url
      });
    } else {
      setEditingUpdate(null);
      setFormData({
        application_id: '',
        enabled: true,
        current_version: '',
        skip: true,
        duration: 120,
        timeout: 0,
        image: '',
        title: 'Внимание!',
        description: '',
        text_button: 'СКАЧАТЬ',
        url: ''
      });
    }
    setOpenDialog(true);
    setError('');
  };

  const handleApplicationChange = (appId) => {
    const selectedApp = applications.find(app => app.id === appId);
    setFormData({
      ...formData,
      application_id: appId,
      url: selectedApp?.market_url || formData.url
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUpdate(null);
    setFormData({
      application_id: '',
      enabled: true,
      current_version: '',
      skip: true,
      duration: 120,
      timeout: 0,
      image: '',
      title: 'Внимание!',
      description: '',
      text_button: 'СКАЧАТЬ',
      url: ''
    });
    setError('');
  };

  const handleSubmit = async () => {
    if (!formData.application_id || !formData.current_version.trim() || !formData.title.trim() || !formData.description.trim() || !formData.url.trim()) {
      setError('Заполните все обязательные поля');
      return;
    }

    setLoading(true);
    try {
      if (editingUpdate) {
        await api.put(`/admin/app-updates/${editingUpdate.id}`, formData);
        setSuccess('Настройка обновления успешно изменена');
      } else {
        await api.post('/admin/app-updates', formData);
        setSuccess('Настройка обновления успешно создана');
      }
      handleCloseDialog();
      loadUpdates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения настройки обновления');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту настройку обновления?')) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/admin/app-updates/${id}`);
      setSuccess('Настройка обновления успешно удалена');
      loadUpdates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Ошибка удаления настройки обновления');
    } finally {
      setLoading(false);
    }
  };

  const getApplicationName = (appId) => {
    const app = applications.find(a => a.id === appId);
    return app ? app.name : `ID: ${appId}`;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            <UpdateIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Обновления приложений
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Управление окнами принудительного обновления для устаревших версий приложений
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          Добавить
        </Button>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Приложение</TableCell>
              <TableCell>Версия</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Можно пропустить</TableCell>
              <TableCell>Таймаут (дни)</TableCell>
              <TableCell align="center">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {updates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    Нет настроек обновлений. Добавьте первую настройку.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              updates.map((update) => (
                <TableRow key={update.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {getApplicationName(update.application_id)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {update.current_version}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={update.enabled ? 'Включено' : 'Выключено'}
                      color={update.enabled ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={update.skip ? 'Да' : 'Нет'}
                      color={update.skip ? 'info' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {update.timeout === 0 ? 'Всегда' : `${update.timeout} дн.`}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(update)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(update.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUpdate ? 'Редактировать настройку обновления' : 'Добавить настройку обновления'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth disabled={!!editingUpdate}>
              <InputLabel>Приложение</InputLabel>
              <Select
                value={formData.application_id}
                onChange={(e) => handleApplicationChange(e.target.value)}
                label="Приложение"
              >
                {applications.map((app) => (
                  <MenuItem key={app.id} value={app.id}>
                    {app.name} ({app.bundle})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Актуальная версия"
              placeholder="202"
              value={formData.current_version}
              onChange={(e) => setFormData({ ...formData, current_version: e.target.value })}
              required
              helperText="Версия приложения, которую нужно установить"
            />

            <TextField
              fullWidth
              label="Заголовок"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />

            <TextField
              fullWidth
              label="Описание"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              helperText="Текст сообщения для пользователя"
            />

            <TextField
              fullWidth
              label="Текст кнопки"
              value={formData.text_button}
              onChange={(e) => setFormData({ ...formData, text_button: e.target.value })}
              required
            />

            <TextField
              fullWidth
              label="Ссылка на маркет"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              required
              helperText="Ссылка на Google Play или App Store"
            />

            <TextField
              fullWidth
              label="URL картинки"
              value={formData.image}
              onChange={(e) => setFormData({ ...formData, image: e.target.value })}
              helperText="Опционально"
            />

            <TextField
              fullWidth
              type="number"
              label="Длительность показа (секунды)"
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
              required
            />

            <TextField
              fullWidth
              type="number"
              label="Не показывать следующие N дней"
              value={formData.timeout}
              onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) })}
              required
              helperText="0 = всегда показывать (чекбокс не отображается)"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.skip}
                  onChange={(e) => setFormData({ ...formData, skip: e.target.checked })}
                />
              }
              label="Можно пропустить обновление"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                />
              }
              label="Включено"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {editingUpdate ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AppUpdatesPage;
